import {
  HeaderNavigationColumn,
  HeaderNavigationItem,
} from '@lib/types/cms/Header';
import { CTA } from '@lib/types/common/CTA';
import { Link } from '@lib/types/common/Link';
import { ButtonStyle } from 'ui-components';
import {
  NavigationColumn,
  NavigationImage,
  NavigationItem,
  NavigationLinks,
} from './types';

const mapNavigationLinks = (
  column: HeaderNavigationColumn & { type: 'links' }
): NavigationLinks => {
  return {
    title: column.title || '',
    highlightFirstLink: column.highlightFirstLink || false,
    items: column.links.map((link) => ({
      value: link.value || '',
      href: link.url || '',
      openInNewTab: link.openInNewTab || false,
    })) as Link[],
  };
};

const mapNavigationImage = (
  column: HeaderNavigationColumn & { type: 'image' }
): NavigationImage => {
  return {
    src: column.imageUrl || '',
    alt: column.imageAlt || '',
    description: column.description || '',
    link: {
      value: column.link.value || '',
      href: column.link.url || '',
    },
  };
};

const mapCta = (column: HeaderNavigationColumn & { type: 'cta' }): CTA => {
  return {
    value: column.value || '',
    href: column.url || '',
    openInNewTab: column.openInNewTab || false,
    appearance: (column.appearance as ButtonStyle) || 'primary',
  };
};

/**
 * Maps CMS-agnostic Header navigation items to the generic NavigationItem type
 * used by the navigation components.
 */
export const headerNavFormat = (
  headerNavItems: HeaderNavigationItem[]
): NavigationItem[] => {
  return headerNavItems.map((navItem): NavigationItem => {
    return {
      mainLink: {
        value: navItem.mainLink?.value || '',
        href: navItem.mainLink?.url || '',
        openInNewTab: navItem.mainLink?.openInNewTab || false,
      },
      isRightAligned: navItem.isRightAligned || false,
      columns:
        navItem.columns
          ?.map((column): NavigationColumn | null => {
            if (!column) return null;

            switch (column.type) {
              case 'links':
                return mapNavigationLinks(column);
              case 'image':
                return mapNavigationImage(column);
              case 'cta':
                return mapCta(column);
              default:
                return null;
            }
          })
          .filter((col): col is NavigationColumn => col !== null) || [],
    };
  });
};
